/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */
import type { GrammarList, Token } from './types';

const negativeAfter = ['binaryOperator', 'unaryOperator', 'openParenthesis'];
const spaceRegex = /^\s*$/;
const identifierRegex = /^[a-zA-Z_.$][(0-9)a-zA-Z0-9_.$]*$/;

const LITERAL = 'literal';
const IDENTIFIER = 'identifier';

// Regular expressions for tokenizing
const escEscapeRegex = /\\\\/;

const preOpRegexElements = [
  // Strings
  "'(?:(?:\\\\')|[^'])*'",
  '"(?:(?:\\\\")|[^"])*"',
  // space
  '\\s+',
  // boolean
  '\\btrue\\b',
  '\\bfalse\\b'
];

const postOpRegexElements = [
  // Identifiers
  '[a-zA-Z_.$][a-zA-Z0-9_.$]*(?:\\(\\d+\\))?(?:\\.[a-zA-Z0-9_.$]+)*',
  // Date format
  '[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}|[0-9]{4}-[0-9]{2}-[0-9]{2}',
  // Numerics (without negative symbol)
  '(?:(?:[0-9]*\\.[0-9]+)|[0-9]+)'
];

const numberRegex = /^-?(?:(?:[0-9]*\.[0-9]+)|[0-9]+)$/;

/**
 * [Lexer]
 * Description : Provides APIs to tokenize and generate the AST for an expression.
 * @class
 */
class Lexer {
  private readonly _grammar: GrammarList;
  private _splitRegex: RegExp | undefined;

  /**
   * Description : Lexer
   * @class
   * @param grammar     Supported grammar.
   */
  constructor(grammar: GrammarList) {
    this._grammar = grammar;
  }

  /**
   * [_isNegative]
   * @static
   * Description : Determines if a token has valid a negative sign.
   * @param tokens      List of tokens
   * @returns true if the negative is valid else false.
   */
  static _isNegative(tokens: Token[]): boolean {
    return negativeAfter.some((operatorType) => operatorType === tokens[tokens.length - 1].tokenType);
  }

  /**
   * [_isWhitespace]
   * @static
   * Description : Checks if the string has all white space characters.
   * @param str         Token string
   * @returns true if all characters are whitespace else false
   */
  static _isWhitespace(str: string): boolean {
    return !!str.match(spaceRegex);
  }

  /**
   * [_createWordboundary]
   * @static
   * Description : Add the word boundaries to the identifiers.
   * @param str         Token string
   * @returns Token after escaping the escape characters.
   */
  static _createWordboundary(str: string): string {
    str = str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (str.match(identifierRegex)) {
      str = `\\b${str}\\b`;
    }
    return str;
  }

  /**
   * [_unquote]
   * @static
   * Description : Unquotes the string.
   * @param str        Token string
   * @returns Unquoted string
   */
  static _unquote(str: string): string {
    const [quote] = str;
    const escQuoteRegex = new RegExp(`\\\\${quote}`, 'g');
    return str
      .substr(1, str.length - 2)
      .replace(escQuoteRegex, quote)
      .replace(escEscapeRegex, '\\');
  }

  /**
   * [_getSplitRegex]
   * Description : Returns the regular expression to split the expression string into elements, which can
   * later be converted to token objects.
   * @private
   * @returns Regular expression to split the expression string.
   *
   */
  _getSplitRegex() {
    if (!this._splitRegex) {
      // Create the element array from the grammar.
      const elemArray = Object.keys(this._grammar)
        .sort((a, b) => {
          return b.length - a.length;
        })
        .map((elem) => {
          return Lexer._createWordboundary(elem);
        }, this);
      // Generate the split expression.
      this._splitRegex = new RegExp(
        `(${[preOpRegexElements.join('|'), elemArray.join('|'), postOpRegexElements.join('|')].join('|')})`
      );
    }
    return this._splitRegex;
  }

  /**
   * [_splitExpression]
   * Description : Splits the flat expression string into elements.
   * @private
   * @param str     Expression string.
   * @returns Elements array
   */
  _splitExpression(str: string) {
    const regex = this._getSplitRegex();
    return str.split(regex).filter((elem) => elem.trim()); // Remove empty strings
  }

  /**
   * [_createToken]
   * Description : Creates a token object for an element split from the expression string.
   * @private
   * @param element    Token String
   * @returns token      Token object
   *                               - tokenType
   *                               - value
   *                               - element
   */
  _createToken(element: string): Token {
    const token: Token = {
      tokenType: LITERAL,
      value: element,
      element
    };
    if (element.startsWith('"') || element.startsWith("'")) {
      token.value = Lexer._unquote(element);
    } else if (element.match(numberRegex)) {
      token.value = parseFloat(element);
    } else if (element === 'true' || element === 'false') {
      token.value = element === 'true';
    } else if (this._grammar[element]) {
      token.tokenType = this._grammar[element].operatorType;
    } else if (element.match(identifierRegex)) {
      token.tokenType = IDENTIFIER;
    } else {
      // empty else block
    }
    return token;
  }

  /**
   * [_getTokens]
   * Description : For each element split from the expression, creates a token object.
   * @private
   * @param elements        Elements split from the expression string.
   * @returns tokens
   */
  _getTokens(elements: string[]): Token[] {
    const tokens: Token[] = [];
    let negate = false;
    for (let i = 0; i < elements.length; i += 1) {
      if (Lexer._isWhitespace(elements[i])) {
        if (tokens.length) {
          tokens[tokens.length - 1].element += elements[i];
        }
      } else if (elements[i] === '-' && Lexer._isNegative(tokens)) {
        negate = true;
      } else {
        if (negate) {
          elements[i] = `-${elements[i]}`;
          negate = false;
        }
        tokens.push(this._createToken(elements[i]));
      }
    }
    return tokens;
  }

  /**
   * [tokenize]
   * Description : Public API on the Lexer class to tokenize an expression.
   * @param exprStr         Expression string.
   * @returns tokens
   */
  tokenize(exprStr: string): Token[] {
    const elements = this._splitExpression(exprStr);
    return this._getTokens(elements);
  }
}

export default Lexer;
